import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Zap,
  Droplets,
  Cable,
  ShieldCheck,
  ScrollText,
  Brain,
  Database,
  ArrowRight,
  ChevronRight,
  Sparkles,
  Building2,
  Factory,
  Landmark,
  Server,
  Globe,
  TrendingUp,
  Clock,
  Target,
  Layers3,
  Flame,
  Thermometer,
  Wind,
  CircuitBoard,
  Atom,
  Truck,
  Search,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  Briefcase,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

// ─────────────────────────────────────────────────────────────
// DATA — THE INVERSION STORY
// ─────────────────────────────────────────────────────────────

const stages = [
  {
    id: "ingest",
    num: "01",
    label: "Wide-Aperture Ingest",
    sub: "Every parcel. Three hard gates. Nothing else filtered.",
    Icon: Search,
    color: "from-sky-500 to-cyan-400",
    accent: "sky",
    accentHex: "#0EA5E9",
    lightBg: "bg-sky-50",
    lightBorder: "border-sky-200",
    dotColor: "bg-sky-500",
    summary:
      "Traditional site selection starts with 10,000 parcels and filters down to 20. That's a losing game — everyone fishes in the same pond. This system inverts the model: ingest everything above 40 acres in target geographies, apply only three binary hard gates, and pass everything else to the solution generators. The addressable universe goes from hundreds to tens of thousands overnight.",
    bullets: [
      "County assessor + GIS data for every parcel in target states",
      "Only three filters: land exists, jurisdiction non-hostile, geographic viability",
      "No power filter — that's an engineering problem, not a gate",
      "No water filter — cooling design eliminates the dependency",
      "Ownership resolution through LLC/trust chains",
      "Continuous scan — new listings surface within 24 hours",
    ],
  },
  {
    id: "power",
    num: "02",
    label: "Power Agent",
    sub: "Don't find sites with power. Find power for sites.",
    Icon: Zap,
    color: "from-amber-500 to-orange-400",
    accent: "amber",
    accentHex: "#F59E0B",
    lightBg: "bg-amber-50",
    lightBorder: "border-amber-200",
    dotColor: "bg-amber-500",
    summary:
      "Power is the binding constraint on 70%+ of sites. Everyone else rejects parcels without utility allocation. This agent runs six parallel pathways — grid interconnection, stranded asset capture, behind-the-meter generation, PPA origination, SMR evaluation, and rapid deployment — and produces a fully-costed, phased power strategy for every candidate parcel in under 60 seconds.",
    bullets: [
      "Six parallel power pathways evaluated simultaneously",
      "Stranded asset detection: retiring coal plants, closed factories, BRAC bases",
      "Behind-the-meter gas, solar+BESS, hybrid microgrid modeling",
      "PPA matching against every project in ISO interconnection queues",
      "Live equipment inventory: generators, transformers, switchgear",
      "Revenue acceleration math: speed-to-MW is the ranking metric",
      "Phased deployment: rapid bridge → permanent → SMR long-term",
    ],
  },
  {
    id: "water",
    num: "03",
    label: "Water Agent",
    sub: "You're not buying water. You're buying thermal rejection.",
    Icon: Droplets,
    color: "from-blue-500 to-cyan-500",
    accent: "blue",
    accentHex: "#3B82F6",
    lightBg: "bg-blue-50",
    lightBorder: "border-blue-200",
    dotColor: "bg-blue-500",
    summary:
      "Water scarcity kills deals in Phoenix, West Texas, and Nevada. But the real need isn't water — it's cooling. Seven pathways from municipal supply through liquid cooling to waste-heat-powered desalination. The agent converts 'water-constrained' into 'water-irrelevant' and turns waste heat into community goodwill that accelerates every permit in the pipeline.",
    bullets: [
      "Seven cooling pathways: municipal, groundwater, reclaimed, air-cooled, liquid cooling, alternative sources, heat monetization",
      "Climate-adjusted 20-year supply risk modeling",
      "Liquid cooling analysis: 1.10 PUE, 80% smaller footprint, near-zero water",
      "Reclaimed water deal structures that solve the utility's problem",
      "Waste heat monetization: schools, hospitals, greenhouses, aquaculture",
      "Cross-agent referral: cooling choice changes power requirement",
      "Community benefit packages auto-generated for rezoning hearings",
    ],
  },
  {
    id: "economics",
    num: "04",
    label: "Deal Economics Engine",
    sub: "Every 'imperfect' site vs. every 'obvious' alternative.",
    Icon: TrendingUp,
    color: "from-emerald-500 to-teal-400",
    accent: "emerald",
    accentHex: "#10B981",
    lightBg: "bg-emerald-50",
    lightBorder: "border-emerald-200",
    dotColor: "bg-emerald-500",
    summary:
      "The Power Agent and Water Agent produce solutions. The Deal Economics Engine rolls them into a single number: total cost to operational MW, compared against the market alternative. An 'imperfect' site in West Texas with behind-the-meter gas and liquid cooling often beats a 'perfect' site in Loudoun County by $200M and 14 months. That math is the decision surface your team acts on.",
    bullets: [
      "Land + power solution + cooling solution + construction = total deal cost",
      "Revenue acceleration value: months saved × revenue per MW per month",
      "Market comparison: your site vs. the 'obvious' alternative in the same region",
      "Portfolio construction: buy 10, develop 6, hold 4 as optionality",
      "Convergence loop: Power ↔ Water iterate until optimized",
      "Every outcome feeds Brain — the system learns from every deal",
    ],
  },
  {
    id: "pipeline",
    num: "05",
    label: "Pipeline + Alerts",
    sub: "The machine that never sleeps.",
    Icon: Target,
    color: "from-violet-500 to-fuchsia-500",
    accent: "violet",
    accentHex: "#8B5CF6",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    dotColor: "bg-violet-500",
    summary:
      "This isn't a tool you query. It's a machine that runs 24/7. Daily scans detect retiring power plants, freed interconnection capacity, available generators, drought escalations, competitor filings, and incentive changes. Monday morning briefings rank the top 10 new opportunities by speed-to-MW. The system found two generators and matched them to a buyer for $2B. It does that scan every single day.",
    bullets: [
      "Daily: stranded asset retirements, queue withdrawals, equipment availability",
      "Daily: drought monitor, aquifer levels, municipal water alerts",
      "Weekly: full parcel universe refresh, new listings, ownership changes",
      "Weekly: Power Opportunity Briefing — top 10 ranked by speed-to-MW",
      "Monthly: utility IRP scan, rate case changes, planned substations",
      "Competitor activity: site plans, interconnection filings, permit applications",
      "Moratorium risk: legislative agenda monitoring, accelerate-or-abort alerts",
    ],
  },
];

const corePrinciples = [
  {
    Icon: TrendingUp,
    color: "from-amber-500 to-orange-500",
    title: "Inversion, not filtration",
    body: "Everyone else filters 10,000 sites to 20. This system takes sites that are 60% qualified and manufactures the missing 40%. The addressable universe grows 50×.",
  },
  {
    Icon: Clock,
    color: "from-emerald-500 to-sky-500",
    title: "Speed is the product",
    body: "Ranked by months-to-first-MW, not site quality score. A B+ site you energize in 14 months beats an A+ site that's 30 months out. Every month of acceleration is $40-80M in captured revenue.",
  },
  {
    Icon: Brain,
    color: "from-violet-500 to-fuchsia-500",
    title: "Solution generation, not sifting",
    body: "Five parallel agents — Power, Water, Fiber, Hardening, Entitlement — take any parcel and output a costed, timed remediation plan. The moat is the solution layer, not the data.",
  },
  {
    Icon: Database,
    color: "from-sky-500 to-indigo-500",
    title: "Compounding memory",
    body: "Every deal outcome — won, lost, passed — feeds back into Brain. After 20 deals, the system knows which utilities move fast, which counties are friendly, which brokers deliver. Institutional knowledge compounds.",
  },
  {
    Icon: DollarSign,
    color: "from-red-500 to-amber-500",
    title: "Underdamp and eat false positives",
    body: "Land is less than 1% of the deal. Buy 10, use 4. Losing 4% across 6 winners is pennies. The cost of waiting is measured in billions of lost deal flow. The system is tuned for recall, not precision.",
  },
  {
    Icon: Layers3,
    color: "from-cyan-500 to-emerald-500",
    title: "Cross-agent convergence",
    body: "Water choice changes power requirement. Power source enables cooling innovation. Heat monetization accelerates permits. Agents iterate until the total deal cost stabilizes. No human team can run this optimization across 200 parcels.",
  },
];

const powerPathways = [
  {
    Icon: CircuitBoard,
    label: "Grid Interconnection",
    detail: "Nearest substation, queue depth, utility speed score, interconnection cost regression from FERC filings.",
  },
  {
    Icon: Factory,
    label: "Stranded Asset Capture",
    detail: "Retiring coal plants, closing factories, BRAC bases. Their interconnections are already built and paid for.",
  },
  {
    Icon: Flame,
    label: "Behind-the-Meter Generation",
    detail: "Gas pipeline proximity, solar+BESS sizing, hybrid microgrid economics, simplified pro forma per parcel.",
  },
  {
    Icon: Wind,
    label: "PPA Origination",
    detail: "Every renewable project in ISO queues — permitted but unfunded. You bring bankable offtake, they bring power.",
  },
  {
    Icon: Atom,
    label: "Small Modular Reactor",
    detail: "NRC-licensed designs, siting requirements, phased deployment: gas bridge today, SMR in 2032.",
  },
  {
    Icon: Truck,
    label: "Rapid Deployment",
    detail: "Live equipment inventory: available generators, mobile substations, cancelled-project turbines. First MW in 10 months.",
  },
];

const waterPathways = [
  {
    Icon: Droplets,
    label: "Municipal Supply",
    detail: "Utility identification, allocation headroom, rate structure, 20-year climate-adjusted supply risk, political risk scoring.",
  },
  {
    Icon: Layers3,
    label: "Groundwater",
    detail: "Aquifer ID, well yield estimates, water quality, regulatory regime, sustainable yield assessment.",
  },
  {
    Icon: Building2,
    label: "Reclaimed Water",
    detail: "Nearest WWTP, available effluent, pipeline cost, deal structures where you solve their infrastructure funding problem.",
  },
  {
    Icon: Wind,
    label: "Air-Cooled (Zero Water)",
    detail: "PUE penalty modeling, capex premium, energy cost, payback vs. water right acquisition. Eliminates the constraint entirely.",
  },
  {
    Icon: CircuitBoard,
    label: "Liquid Cooling",
    detail: "1.05-1.15 PUE, 80% smaller building, near-zero water. Higher density means smaller parcels qualify. The future, built today.",
  },
  {
    Icon: Globe,
    label: "Alternative Sources",
    detail: "Brackish desal, produced water from O&G, rainwater harvesting, agricultural water rights, waste-heat-powered desalination.",
  },
  {
    Icon: Thermometer,
    label: "Heat Monetization",
    detail: "Turn waste heat into revenue and community goodwill. Schools, hospitals, greenhouses. The play that wins rezoning votes.",
  },
];

const alertTypes = [
  { color: "bg-amber-500", label: "STRANDED_ASSET_DETECTED", example: "AES Cayuga 306MW coal plant retirement moved to Q2 2027. 138kV interconnection. 3 parcels within 4 miles." },
  { color: "bg-emerald-500", label: "QUEUE_WITHDRAWAL", example: "180MW solar withdrew from PJM queue at Possum Point. 180MW freed. Your Stafford County parcel is 12 miles away." },
  { color: "bg-sky-500", label: "GENERATOR_AVAILABLE", example: "2x GE LM6000PF+ listed. 47MW each, low hours. Matches your Midland TX site requirement." },
  { color: "bg-violet-500", label: "UTILITY_EXPANSION", example: "Oncor filed for 345kV substation in Ector County. Your Odessa parcel moves to 400MW available in 30 months." },
  { color: "bg-red-500", label: "MORATORIUM_RISK", example: "Loudoun County Board agenda: data center moratorium discussion June 15. You have 2 parcels in LOI stage — ACCELERATE." },
  { color: "bg-blue-500", label: "DROUGHT_ESCALATION", example: "Maricopa County moved to D3 Extreme. Municipal curtailment probability 40%. Accelerate liquid cooling conversion." },
];

const stakeholders = [
  {
    title: "Development Team",
    icon: Factory,
    blurb: "Your team sees the pipeline: parcels ranked by speed-to-MW, power and water solutions pre-modeled, deal economics assembled. Decision in hours, not quarters.",
    policies: ["Power Strategy", "Water Strategy", "Deal Economics"],
    value: "Every parcel arrives with a fully-costed phased deployment plan. The team evaluates solutions, not raw land.",
  },
  {
    title: "Engineering Partner",
    icon: Briefcase,
    blurb: "Your partner — the one who built Switch, Aligned, and Colossus — sees the Power Solution Matrix and says 'I can build that' or 'swap those turbines.' His expertise amplifies the AI, not the other way around.",
    policies: ["Behind-the-Meter", "Rapid Deploy", "Liquid Cooling"],
    value: "The AI surfaces opportunities. The builder decides how to execute. Speed multiplied by experience.",
  },
  {
    title: "Capital Partners",
    icon: Landmark,
    blurb: "BlackRock, KKR, Blackstone — they buy finished campuses. Show them a portfolio of 20 sites with phased power strategies, community benefit packages, and revenue projections. They write the check tomorrow.",
    policies: ["Portfolio Risk", "Revenue Acceleration", "Exit Timing"],
    value: "Governed intelligence over an asset class. Not a pitch deck — an operating system for infrastructure capital.",
  },
  {
    title: "Hyperscaler Tenants",
    icon: Server,
    blurb: "Microsoft, Google, Meta, CoreWeave — they need megawatts yesterday. Show them a site that's 10 months to first MW with behind-the-meter gas and a PPA overlay. They're signing before the grid interconnection would have even started.",
    policies: ["Speed-to-MW", "Renewable Mix", "Expansion Path"],
    value: "The tenant cares about when they get power, not how you solved the problem. Speed is the entire pitch.",
  },
  {
    title: "Utilities + Municipalities",
    icon: Building2,
    blurb: "You're not asking for power — you're solving their problem. Anchor tenant for a planned substation. Revenue for a struggling water utility. Free heat for the school district. Walk in as a partner.",
    policies: ["Community Benefit", "Utility Partnership", "Heat Monetization"],
    value: "The AI generates the deal structure that makes the utility say yes. Load growth they need, revenue they want, community goodwill they crave.",
  },
  {
    title: "Permitting + Government",
    icon: Globe,
    blurb: "County commissioners see jobs, tax revenue, and free heat to every school in the district. The AI auto-generates community benefit packages from waste heat analysis. Rezoning approval probability jumps from 55% to 92%.",
    policies: ["Entitlement Strategy", "Community Benefit", "Environmental"],
    value: "Transform opposition into invitation. The AI models what the community needs and shows how your project delivers it.",
  },
];

const progression = ["Ingest", "Power", "Water", "Economics", "Pipeline"];

const comparisonData = {
  headers: ["", "Traditional Site Selection", "Land Creation Engine"],
  rows: [
    ["Approach", "Find sites that already qualify", "Manufacture qualification for sites others overlook"],
    ["Universe", "50 known-good sites (crowded)", "10,000+ sites nobody else evaluates"],
    ["Houston?", "Rejected — hurricane zone", "Hardening cost < competing for scarce Virginia inventory"],
    ["Phoenix?", "Rejected — water scarce", "Liquid cooling eliminates water. Cheap power, land, fiber remain."],
    ["No substation?", "Rejected — no power", "Behind-the-meter gas + PPA + rapid deploy = power in 10 months"],
    ["Evaluation time", "6 months per site", "Fully-costed solution package in hours"],
    ["Ranking", "Best site (subjective)", "Speed to operational MW (measurable)"],
    ["Portfolio", "Pick the winner", "Buy 10, use 6. The 4 you don't use cost less than one quarter of delay."],
  ],
};

// Power Solution Matrix data — split into capex and energy columns
const matrixRows = [
  { pathway: "Grid",         mw: "80 MW",  timeline: "28 months", capex: "$225K/MW",  energy: "$65/MWh",  rank: "4", highlight: false },
  { pathway: "Behind-meter", mw: "200 MW", timeline: "16 months", capex: "$1.6M/MW",  energy: "$45/MWh",  rank: "2", highlight: false },
  { pathway: "PPA Match",    mw: "150 MW", timeline: "12 months", capex: "—",          energy: "$42/MWh",  rank: "3", highlight: false },
  { pathway: "SMR",          mw: "300 MW", timeline: "72 months", capex: "$8M/MW",     energy: "$90/MWh",  rank: "5", highlight: false },
  { pathway: "Rapid Deploy", mw: "200 MW", timeline: "10 months", capex: "$2.1M/MW",   energy: "$55/MWh",  rank: "1", highlight: true  },
];

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

function SectionLabel({ index, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {String(index).padStart(2, "0")} / {children}
    </p>
  );
}

function StageButton({ stage, active, onClick, showConnector }) {
  const { Icon } = stage;
  const isActive = active.id === stage.id;

  return (
    <>
      <button
        onClick={() => onClick(stage)}
        className={`w-full rounded-[28px] border p-5 text-left transition-all duration-200 ${
          isActive
            ? "border-zinc-900 bg-zinc-900 text-white shadow-2xl dark:border-white dark:bg-white dark:text-zinc-900"
            : "border-zinc-200 bg-white/80 hover:border-zinc-300 hover:bg-white dark:border-zinc-800 dark:bg-zinc-900/70 dark:hover:border-zinc-600"
        }`}
      >
        <div className="flex items-start gap-4">
          <div
            className={`shrink-0 rounded-2xl bg-gradient-to-br ${stage.color} p-3 text-white shadow-lg`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div
                  className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                    isActive ? "opacity-50" : "text-zinc-400"
                  }`}
                >
                  {stage.num}
                </div>
                <div className="mt-0.5 text-[17px] font-semibold leading-tight">
                  {stage.label}
                </div>
              </div>
              <ArrowRight
                className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                  isActive ? "translate-x-1 opacity-60" : "opacity-30"
                }`}
              />
            </div>
            <p
              className={`mt-1.5 text-sm leading-relaxed ${
                isActive ? "text-zinc-300 dark:text-zinc-600" : "text-zinc-500"
              }`}
            >
              {stage.sub}
            </p>
          </div>
        </div>
      </button>
      {showConnector && (
        <div className="ml-[30px] h-5 w-px bg-zinc-200 dark:bg-zinc-700" />
      )}
    </>
  );
}

function DetailPanel({ stage }) {
  const { Icon } = stage;
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={stage.id}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.26 }}
        className="h-full"
      >
        <Card className="h-full rounded-[32px] border-0 bg-gradient-to-br from-white to-zinc-50 shadow-xl dark:from-zinc-900 dark:to-zinc-950">
          <CardContent className="p-8 lg:p-10">
            <div className="mb-6 flex items-center gap-4">
              <div
                className={`rounded-3xl bg-gradient-to-br ${stage.color} p-4 text-white shadow-xl`}
              >
                <Icon className="h-8 w-8" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
                  Stage {stage.num}
                </p>
                <h3 className="mt-1 text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                  {stage.label}
                </h3>
              </div>
            </div>

            <p className="mb-8 text-sm leading-7 text-zinc-600 dark:text-zinc-300">
              {stage.summary}
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              {stage.bullets.map((item) => (
                <div
                  key={item}
                  className={`flex items-start gap-3 rounded-2xl border ${stage.lightBorder} ${stage.lightBg} p-4 dark:border-zinc-800 dark:bg-zinc-900/50`}
                >
                  <div
                    className={`mt-1 h-2 w-2 shrink-0 rounded-full ${stage.dotColor}`}
                  />
                  <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}

function PathwayGrid({ pathways, title, color }) {
  return (
    <div>
      <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-zinc-500">
        {title}
      </h4>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pathways.map((pw, i) => {
          const { Icon } = pw;
          return (
            <motion.div
              key={pw.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.04 }}
              viewport={{ once: true }}
            >
              <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                <CardContent className="p-5">
                  <div className={`mb-3 inline-flex rounded-2xl bg-gradient-to-br ${color} p-2.5 text-white shadow-md`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <h5 className="mb-2 text-sm font-bold text-zinc-900 dark:text-zinc-100">
                    {pw.label}
                  </h5>
                  <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                    {pw.detail}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function StakeholderView() {
  const [selected, setSelected] = useState(stakeholders[0].title);
  const current = stakeholders.find((s) => s.title === selected) || stakeholders[0];
  const Icon = current.icon;

  return (
    <div className="grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
      <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl dark:bg-zinc-900">
        <CardContent className="p-6 lg:p-8">
          <div className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">
            Stakeholders
          </div>
          <div className="space-y-3">
            {stakeholders.map((item) => {
              const ActiveIcon = item.icon;
              const active = item.title === selected;
              return (
                <button
                  key={item.title}
                  onClick={() => setSelected(item.title)}
                  className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-all ${
                    active
                      ? "border-white/20 bg-white/10"
                      : "border-white/10 bg-white/5 hover:bg-white/[0.08]"
                  }`}
                >
                  <div className="rounded-2xl bg-white/10 p-2.5">
                    <ActiveIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold">{item.title}</div>
                    <div className="mt-1 text-sm leading-6 text-zinc-300">
                      {item.blurb}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.title}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="grid gap-6"
        >
          <Card className="rounded-[32px] border-0 bg-gradient-to-br from-white to-zinc-100 shadow-2xl dark:from-zinc-900 dark:to-zinc-950">
            <CardContent className="p-8 lg:p-10">
              <div className="flex items-center gap-4">
                <div className="rounded-3xl bg-zinc-900 p-4 text-white shadow-xl dark:bg-white dark:text-zinc-900">
                  <Icon className="h-8 w-8" />
                </div>
                <div>
                  <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">
                    Stakeholder view
                  </div>
                  <h3 className="mt-1 text-3xl font-semibold tracking-tight">
                    {current.title}
                  </h3>
                </div>
              </div>

              <p className="mt-6 max-w-3xl text-base leading-7 text-zinc-600 dark:text-zinc-300">
                {current.value}
              </p>

              <div className="mt-8 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-3xl border bg-white/80 p-5 shadow-sm dark:bg-zinc-900/70">
                  <div className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-500">
                    Policy domains
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {current.policies.map((policy) => (
                      <Badge key={policy} className="rounded-full px-3 py-1 text-sm">
                        {policy}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border bg-white/80 p-5 shadow-sm dark:bg-zinc-900/70">
                  <div className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-500">
                    Why this view matters
                  </div>
                  <p className="mt-4 text-sm leading-7 text-zinc-600 dark:text-zinc-300">
                    Each stakeholder sees the same parcel data through a different
                    lens. The developer sees speed-to-MW. The capital partner sees
                    portfolio risk. The municipality sees community benefit.
                    Same corpus, governed by policy.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {stakeholders.map((item, i) => {
              const SmallIcon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: i * 0.04 }}
                  viewport={{ once: true }}
                >
                  <Card
                    className={`h-full rounded-[28px] border shadow-sm ${
                      item.title === current.title
                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                        : "bg-white/80 dark:bg-zinc-900/70"
                    }`}
                  >
                    <CardContent className="p-5">
                      <div
                        className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${
                          item.title === current.title
                            ? "bg-white/10 dark:bg-zinc-200"
                            : "bg-zinc-100 dark:bg-zinc-800"
                        }`}
                      >
                        <SmallIcon className="h-5 w-5" />
                      </div>
                      <h3 className="text-lg font-semibold">{item.title}</h3>
                      <p
                        className={`mt-2 text-sm leading-6 ${
                          item.title === current.title
                            ? "text-zinc-200 dark:text-zinc-700"
                            : "text-zinc-600 dark:text-zinc-300"
                        }`}
                      >
                        {item.blurb}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function StoryView({ activeStage, setActiveStage }) {
  return (
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
      <div>
        {stages.map((stage, idx) => (
          <StageButton
            key={stage.id}
            stage={stage}
            active={activeStage}
            onClick={setActiveStage}
            showConnector={idx < stages.length - 1}
          />
        ))}
      </div>
      <DetailPanel stage={activeStage} />
    </div>
  );
}

function ComparisonTable() {
  return (
    <div className="overflow-x-auto rounded-[24px] border bg-white shadow-sm dark:bg-zinc-900/70">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-zinc-50 dark:bg-zinc-800/50">
            {comparisonData.headers.map((h, i) => (
              <th
                key={i}
                className={`px-6 py-4 text-left font-semibold ${
                  i === 0
                    ? "text-zinc-500 w-[160px]"
                    : i === 1
                    ? "text-zinc-600 dark:text-zinc-300"
                    : "text-emerald-700 dark:text-emerald-400"
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {comparisonData.rows.map((row, ri) => (
            <tr key={ri} className="border-b last:border-0">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`px-6 py-4 ${
                    ci === 0
                      ? "font-semibold text-zinc-700 dark:text-zinc-300"
                      : ci === 1
                      ? "text-zinc-500 dark:text-zinc-400"
                      : "font-medium text-zinc-900 dark:text-zinc-100"
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────

export default function LandManagementStory() {
  const [activeStage, setActiveStage] = useState(stages[0]);
  const [view, setView] = useState("story");

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 text-zinc-900 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">

        {/* ── HERO ─────────────────────────────────────────────── */}
        <section className="py-16 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide">
                From land sifting to land creation
              </Badge>
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight sm:text-6xl lg:text-7xl">
                <span className="whitespace-nowrap">Don't find</span>{" "}
                <span className="text-zinc-400">qualified sites.</span>
                <br />
                <span className="whitespace-nowrap">Qualify more</span>{" "}
                sites.
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-zinc-500 dark:text-zinc-400">
                AI-powered land creation engine for data center development.
                Take sites that are 60% qualified and manufacture the missing
                40% — power, water, fiber, hardening, entitlements — faster
                than anyone else in the market.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {progression.map((step, i) => (
                  <div key={step} className="flex items-center gap-3">
                    <div className="rounded-full border bg-white px-4 py-2 text-sm font-medium shadow-sm dark:bg-zinc-900">
                      {step}
                    </div>
                    {i < progression.length - 1 && (
                      <ArrowRight className="h-4 w-4 text-zinc-400" />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Card className="rounded-[28px] border-0 bg-zinc-900 text-white shadow-2xl dark:bg-zinc-900">
                <CardContent className="p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
                    Core thesis
                  </p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    Every site has deficiencies. The question isn't "does it
                    pass?" — it's "what would it take to make it pass, and can
                    we do that faster than anyone else?"
                  </p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    Land is less than 1% of the deal. Speed is everything.
                    Buy 10, use 6. The 4 you don't use cost less than one
                    quarter of delay.
                  </p>
                  <p className="text-sm leading-7 text-zinc-300">
                    The AI doesn't add friction — it manufactures solutions.
                    Power, water, cooling, permits — there is always a way.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* ── 01 / SYSTEM PROGRESSION ──────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <SectionLabel index={1}>System Progression</SectionLabel>
              <h2 className="text-3xl font-bold tracking-tight">
                Five stages. One creation engine.
              </h2>
              <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
                Each stage adds capability. The platform core — the LLM, the
                policy engine, the temporal database — never changes. Only what
                the agents analyze expands.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setView("story")}
                className="rounded-2xl px-5"
                variant={view === "story" ? "default" : "outline"}
              >
                System View
              </Button>
              <Button
                onClick={() => setView("stakeholder")}
                className="rounded-2xl px-5"
                variant={view === "stakeholder" ? "default" : "outline"}
              >
                Stakeholder View
              </Button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
            >
              {view === "story" ? (
                <StoryView
                  activeStage={activeStage}
                  setActiveStage={setActiveStage}
                />
              ) : (
                <StakeholderView />
              )}
            </motion.div>
          </AnimatePresence>
        </section>

        {/* ── 02 / CORE PRINCIPLES ─────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={2}>Core Principles</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              Why this isn't a me-too.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              The moat isn't the data — everyone can get county GIS data. The
              moat is the solution-generation layer and the compounding
              institutional memory.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {corePrinciples.map((pillar, i) => {
              const { Icon } = pillar;
              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.06 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full rounded-[28px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-6">
                      <div
                        className={`mb-5 inline-flex rounded-2xl bg-gradient-to-br ${pillar.color} p-3 text-white shadow-lg`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mb-2 text-base font-bold text-zinc-900 dark:text-zinc-100">
                        {pillar.title}
                      </h3>
                      <p className="text-sm leading-7 text-zinc-500 dark:text-zinc-400">
                        {pillar.body}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── 03 / POWER PATHWAYS ──────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={3}>Agent Deep Dive</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              Six ways to power. Seven ways to cool.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every candidate parcel gets all pathways evaluated in parallel.
              The output isn't a score — it's a fully-costed, phased deployment
              strategy with a speed comparison against the market alternative.
            </p>
          </div>

          <div className="space-y-10">
            <PathwayGrid
              pathways={powerPathways}
              title="⚡ Power Agent — 6 Pathways"
              color="from-amber-500 to-orange-400"
            />
            <PathwayGrid
              pathways={waterPathways}
              title="💧 Water Agent — 7 Pathways"
              color="from-blue-500 to-cyan-500"
            />
          </div>
        </section>

        {/* ── 04 / COMPARISON TABLE ────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={4}>The Inversion</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              Traditional approach vs. land creation engine.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every row is a site that competitors reject and this system
              converts into a deal.
            </p>
          </div>

          <ComparisonTable />
        </section>

        {/* ── 05 / LIVE ALERTS ─────────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={5}>24/7 Intelligence</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              The machine that never sleeps.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Daily scans, weekly briefings, instant alerts. The system found
              two generators and matched them to CoreWeave for $2B. It runs
              that scan every single day.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {alertTypes.map((alert, i) => (
              <motion.div
                key={alert.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                  <CardContent className="p-5">
                    <div className="mb-3 flex items-center gap-2">
                      <div className={`h-2.5 w-2.5 rounded-full ${alert.color}`} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                        {alert.label}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                      {alert.example}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── 06 / SAMPLE POWER SOLUTION MATRIX ────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={6}>Decision Surface</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              What your team actually sees.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Not a score. Not a ranking. A Power Solution Matrix with phased
              deployment strategy and market comparison — the artifact that
              moves a deal.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="p-8 lg:p-10">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-400 p-3 text-white shadow-lg">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                      Power Solution Matrix
                    </p>
                    <h3 className="text-xl font-bold">
                      Parcel: AZ-Maricopa-4471
                    </h3>
                  </div>
                </div>

                {/* ── Two-column cost table ── */}
                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Pathway</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">MW</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Timeline</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Capex / MW</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Energy $/MWh</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Rank</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {matrixRows.map((row) => (
                        <tr
                          key={row.pathway}
                          className={
                            row.highlight
                              ? "bg-emerald-500/10"
                              : "border-b border-white/5"
                          }
                        >
                          <td className={`px-4 py-3 ${row.highlight ? "font-semibold text-emerald-400" : ""}`}>
                            {row.highlight ? "⭐ " : ""}{row.pathway}
                          </td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.mw}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.timeline}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.capex}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.energy}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "font-bold text-emerald-400" : "text-zinc-500"}`}>{row.rank}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-3 text-xs text-zinc-600">
                  Illustrative analysis. Actual values generated per-parcel by
                  the Power Agent from live connector data. PPA shows no capex
                  — it is a contracted energy rate, not a capital expenditure.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                      Recommended strategy
                    </p>
                    <div className="space-y-2 text-sm text-zinc-300">
                      <p><span className="font-semibold text-emerald-400">Phase 1:</span> Rapid deploy 4× LM6000 — 10 months to 200MW</p>
                      <p><span className="font-semibold text-amber-400">Phase 2:</span> Behind-meter gas permanent — 16 months</p>
                      <p><span className="font-semibold text-sky-400">Phase 3:</span> PPA from Sonoran Solar II — 12 months, 150MW</p>
                      <p><span className="font-semibold text-zinc-400">Phase 4:</span> Grid interconnection completes — 28 months, 80MW</p>
                      <p><span className="font-semibold text-violet-400">Phase 5:</span> SMR evaluation for 2032+ expansion</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                      vs. "Perfect" site in Loudoun County
                    </p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Time to first MW</span>
                        <span className="font-bold text-emerald-400">14 months faster</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Total deal cost</span>
                        <span className="font-bold text-emerald-400">$310M cheaper</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Revenue acceleration</span>
                        <span className="font-bold text-emerald-400">+$538M captured</span>
                      </div>
                      <div className="mt-2 border-t border-white/10 pt-3">
                        <p className="text-xs text-zinc-500">
                          This "imperfect" site outperforms the obvious market
                          alternative by every metric that matters.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── CLOSING FRAME ────────────────────────────────────── */}
        <section className="pt-8 pb-4">
          <Card className="rounded-[32px] border-0 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white shadow-2xl">
            <CardContent className="p-8 lg:p-10">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">
                Closing frame
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">
                Give us 20 more properties and they'll buy them tomorrow.
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-7 text-zinc-300">
                The AI doesn't replace the builder who did Switch, Aligned, and
                Colossus. It feeds him 200 opportunities a month instead of 5.
                With power solutions pre-modeled, cooling strategies pre-costed,
                and community benefit packages pre-written. The only thing
                between this team and billions in deal flow is finding the dirt
                fast enough. That's what this system does.
              </p>
              <p className="mt-4 text-base leading-7 text-zinc-400">
                There is always a way. The machine finds it.
              </p>
            </CardContent>
          </Card>
        </section>

      </div>
    </div>
  );
}